/**
 * Custom-field values (Banner Description, Key Takeaways, ...) in the store, and the rules that
 * decide which value wins when the same field arrives from several places:
 *
 *  - the user typing (`merge`): always wins, marks the key dirty (unsaved / unpublished edit)
 *  - the Quattr draft restored from S3 (`fromPost`, source 'draft'): what the user typed last time
 *  - the WordPress post (`fromPost`, source 'post'): the live values, refetched after every rescore
 *    and publish; WordPress reports EVERY configured field, so a field the customer never filled
 *    arrives as '' and must never replace a real value or get saved back
 *
 * Seeds (both sources) only ever fill a field the user has not typed in. Between the two seeds:
 * a draft edit that was never published beats the post (the post still has the old value), and
 * the post beats a draft that was already published (WordPress may have been edited since).
 */

export type CustomFieldSeedSource = 'post' | 'draft';

export interface CustomFieldsState {
  customFieldsValues: Record<string, string>;
  /** Keys the user edited in this session and has not published yet. */
  customFieldsDirty: Record<string, true>;
  /** Where the current (non user typed) value of a key came from. */
  customFieldsSeedSource: Record<string, CustomFieldSeedSource>;
  /** Draft keys whose value differs from what was last published. */
  customFieldsDraftUnpublished: Record<string, true>;
}

export interface SetCustomFieldsValuesAction {
  value?: unknown;
  /** User edit: only the given keys change. */
  merge?: boolean;
  /** Seed from the post or the draft (see rules above). */
  fromPost?: boolean;
  source?: CustomFieldSeedSource;
  /** Draft seed only: keys whose draft value was never published. */
  unpublishedKeys?: string[];
  /** After a successful publish: the values are now on WordPress, nothing is pending. */
  clearDirty?: boolean;
}

export const emptyCustomFieldsState = (): CustomFieldsState => ({
  customFieldsValues: {},
  customFieldsDirty: {},
  customFieldsSeedSource: {},
  customFieldsDraftUnpublished: {},
});

const isFilled = (v: unknown): v is string => typeof v === 'string' && v.trim() !== '';

const asMap = (value: unknown): Record<string, string> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, String(v ?? '')]))
    : {};

export const applyCustomFieldsAction = (
  state: Partial<CustomFieldsState> | null | undefined,
  action: SetCustomFieldsValuesAction
): CustomFieldsState => {
  const prev: CustomFieldsState = { ...emptyCustomFieldsState(), ...(state || {}) };
  const incoming = asMap(action.value);

  if (action.clearDirty) {
    // Everything on screen is now on WordPress: no dirty keys, no unpublished draft edits, and the
    // post is the reference for these keys from here on.
    const seedSource = { ...prev.customFieldsSeedSource };
    for (const key of Object.keys(prev.customFieldsValues)) seedSource[key] = 'post';
    return {
      ...prev,
      customFieldsDirty: {},
      customFieldsDraftUnpublished: {},
      customFieldsSeedSource: seedSource,
    };
  }

  if (action.merge) {
    const dirty = { ...prev.customFieldsDirty };
    for (const key of Object.keys(incoming)) dirty[key] = true;
    return { ...prev, customFieldsValues: { ...prev.customFieldsValues, ...incoming }, customFieldsDirty: dirty };
  }

  if (action.fromPost) {
    const source: CustomFieldSeedSource = action.source === 'draft' ? 'draft' : 'post';
    const unpublished = new Set(action.unpublishedKeys || []);
    const values = { ...prev.customFieldsValues };
    const seedSource = { ...prev.customFieldsSeedSource };
    const draftUnpublished = { ...prev.customFieldsDraftUnpublished };
    for (const [key, value] of Object.entries(incoming)) {
      if (!isFilled(value)) continue; // '' from WordPress is "not set", never a value
      if (prev.customFieldsDirty[key]) continue; // the user typed here
      const current = values[key];
      const currentSource = seedSource[key];
      let take = !isFilled(current);
      // An unpublished draft edit beats the post (the post still has the old value).
      if (!take && source === 'draft' && unpublished.has(key)) take = true;
      // The post (refetched after every rescore / publish) beats an older post value and a draft value
      // that was already published: WordPress may have been edited since.
      if (
        !take &&
        source === 'post' &&
        (currentSource === 'post' || (currentSource === 'draft' && !draftUnpublished[key]))
      ) {
        take = true;
      }
      if (!take) continue;
      values[key] = value;
      seedSource[key] = source;
      if (source === 'draft' && unpublished.has(key)) draftUnpublished[key] = true;
      else delete draftUnpublished[key];
    }
    return {
      ...prev,
      customFieldsValues: values,
      customFieldsSeedSource: seedSource,
      customFieldsDraftUnpublished: draftUnpublished,
    };
  }

  // Plain set / reset (page open, page close): replace everything, nothing is dirty.
  return { ...emptyCustomFieldsState(), customFieldsValues: incoming };
};

/**
 * Values to write to WordPress on publish: every filled field, plus a field the user cleared on
 * purpose (dirty and empty). Fields WordPress reported as '' and the user never touched are left
 * out so a publish never blanks something by accident.
 */
export const customFieldsForPublish = (
  values: Record<string, string> | null | undefined,
  dirty: Record<string, true> | null | undefined
): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(values || {})) {
    if (isFilled(value) || dirty?.[key]) out[key] = String(value ?? '');
  }
  return out;
};

/** Draft keys whose stored value differs from the last published one (or were never published). */
export const unpublishedDraftKeys = (
  draft: Record<string, string> | null | undefined,
  published: Record<string, string> | null | undefined
): string[] =>
  Object.entries(draft || {})
    .filter(([key, value]) => isFilled(value) && (published?.[key] ?? '') !== value)
    .map(([key]) => key);
